import { createSlice } from '@reduxjs/toolkit';
import { QuestionnaireState } from 'src/models';
import { asyncIsFulfilled, asyncIsPending, asyncIsRejected } from '../asyncConfig';
import {
  getClientKYCFiles,
  getClientQuestionnaires, getCountries,
  getKYCProgress,
  getKYCSectionMatch, getKYCSectionTitles, getKYCServicesTypes,
  getMyProfile, submitQuestionnaireAns,
  verifyToken
} from './questionnaireService';

const initialState: QuestionnaireState = {
  isAuthenticated: null,
  message: '',
  status: null,
  questionnaires: [],
  countries: [],
  kyc_section_titles: [],
  docs_uploaded: [],
  docs_to_upload: {},
  tabIndex: 0,
  activeTitleId: null,
  client: null,
  matches: [],
  visa_types: [],
}

export const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    clearApplicationState: (state) => {
      state.status = null;
      state.isAuthenticated = null
      state.message = ''
    },
    unAuthenticate: (state) => {
      state.status = null;
      state.isAuthenticated = false
      state.message = ''
    },
    getClientProfile: (state, action) => {
      state.isAuthenticated = null
      const client = JSON.parse(localStorage.getItem('daps-client-spa') ?? '{}')
      if (Object.keys(client).length > 1 && client.uuid === action.payload) {
        state.client = client
      } else {
        state.isAuthenticated = false;
      }
      state.status = null;
      state.message = ''
    },
    setTabIndex: (state, action) => {
      state.status = null;
      state.message = '';
      state.tabIndex = action.payload;
    },
    setActiveTitleId: (state, action) => {
      state.status = null;
      state.message = '';
      state.activeTitleId = action.payload;
    },
    resetDocsToUpload: (state, action) => {
      state.status = null;
      state.message = '';
      state.docs_to_upload = action.payload;
    },
    resetUploadedDocs: (state, action) => {
      state.status = null;
      state.message = '';
      state.docs_uploaded = action.payload;
    },
    resetQuestionnairesWithNewAnswers: (state, action) => {
      state.status = null;
      state.message = '';
      state.questionnaires = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(submitQuestionnaireAns.pending, (state, _) => {
      state.status = 'pending';
      state.message = '';
      state.action = 'submit';
    })
    builder.addCase(submitQuestionnaireAns.rejected, asyncIsRejected)
    builder.addCase(submitQuestionnaireAns.fulfilled, asyncIsFulfilled)
    builder.addCase(getKYCProgress.pending, asyncIsPending)
    builder.addCase(getKYCProgress.rejected, asyncIsRejected)
    builder.addCase(getKYCProgress.fulfilled, (state, action) => {
      console.log('KYC Progress Response:', action.payload);
      state.status = null;
      state.isAuthenticated = true;
      // Transform the new API response to match the existing structure
      state.questionnaires = action.payload.questionnaires.map((item: any) => ({
        id: item.id,
        questionnaire_id: item.questionnaire_id,
        title_id: item.title_id,
        section_title: item.section_title,
        questions: item.questionnaires, // This is the nested questionnaires array
        migration_services_types: item.migration_services_types,
        is_active: true,
        createdAt: '',
        updatedAt: ''
      }));
      // Set the activeTitleId to the first questionnaire's title_id
      if (action.payload.questionnaires.length > 0) {
        state.activeTitleId = action.payload.questionnaires[0].title_id;
      }
      console.log('Transformed questionnaires:', state.questionnaires);
      console.log('Active title ID:', state.activeTitleId);
    })
    builder.addCase(getClientQuestionnaires.pending, asyncIsPending)
    builder.addCase(getClientQuestionnaires.rejected, asyncIsRejected)
    builder.addCase(getClientQuestionnaires.fulfilled, (state, action) => {
      state.status = null;
      state.isAuthenticated = true;
      state.questionnaires = action.payload;
    })
    builder.addCase(getClientKYCFiles.pending, asyncIsPending)
    builder.addCase(getClientKYCFiles.rejected, asyncIsRejected)
    builder.addCase(getClientKYCFiles.fulfilled, (state, action) => {
      state.status = null;
      state.isAuthenticated = true;
      state.docs_uploaded = action.payload;
    })
    builder.addCase(getKYCServicesTypes.pending, asyncIsPending)
    builder.addCase(getKYCServicesTypes.rejected, asyncIsRejected)
    builder.addCase(getKYCServicesTypes.fulfilled, (state, action) => {
      state.status = null;
      state.visa_types = action.payload;
    })
    builder.addCase(getCountries.pending, asyncIsPending)
    builder.addCase(getCountries.rejected, asyncIsRejected)
    builder.addCase(getCountries.fulfilled, (state, action) => {
      state.status = null;
      state.countries = action.payload.countries;
    })
    builder.addCase(getKYCSectionMatch.pending, asyncIsPending)
    builder.addCase(getKYCSectionMatch.rejected, asyncIsRejected)
    builder.addCase(getKYCSectionMatch.fulfilled, (state, action) => {
      state.status = null;
      state.matches = action.payload;
    })
    builder.addCase(getKYCSectionTitles.pending, asyncIsPending)
    builder.addCase(getKYCSectionTitles.rejected, asyncIsRejected)
    builder.addCase(getKYCSectionTitles.fulfilled, (state, action) => {
      state.status = null;
      state.kyc_section_titles = action.payload;
    })
    builder.addCase(getMyProfile.pending, asyncIsPending)
    builder.addCase(getMyProfile.rejected, asyncIsRejected)
    builder.addCase(getMyProfile.fulfilled, (state, action) => {
      if (action.payload.client) {
        localStorage.setItem('daps-client-spa', JSON.stringify(action.payload.client))
      }
      state.status = null;
      state.isAuthenticated = true;
      state.client = action.payload.client;
    })
    builder.addCase(verifyToken.pending, asyncIsPending)
    builder.addCase(verifyToken.rejected, asyncIsRejected)
    builder.addCase(verifyToken.fulfilled, (state, action) => {
      if (action.payload.client) {
        localStorage.setItem('daps-client-spa', JSON.stringify(action.payload.client))
      }
      state.status = 'fulfilled';
      state.message = action.payload.message;
      state.isAuthenticated = false;
      state.client = action.payload.client;
    })
  }
});

export const {
  getClientProfile,
  clearApplicationState,
  setActiveTitleId,
  setTabIndex,
  resetQuestionnairesWithNewAnswers,
  resetDocsToUpload,
  resetUploadedDocs,
  unAuthenticate
} = questionnaireSlice.actions;
export default questionnaireSlice.reducer;